import { useEffect } from 'react';

const NATIVE_GESTURE_TARGETS = 'input, textarea, select, canvas, [contenteditable]:not([contenteditable="false"]), [role="slider"]';
const GESTURE_THRESHOLD = 8;

function hasTextSelection() {
  const selection = window.getSelection();
  return selection && !selection.isCollapsed;
}

function ownsHorizontalGesture(target, boundary) {
  for (let element = target; element instanceof Element; element = element.parentElement) {
    if (element.matches(NATIVE_GESTURE_TARGETS)) return true;
    const style = window.getComputedStyle(element);
    // Respect custom swipe/drag controls and intentional native horizontal lists.
    if (/\b(none|pan-x|pan-left|pan-right)\b/.test(style.touchAction || style.getPropertyValue('touch-action'))) return true;
    if (/^(auto|scroll|overlay)$/.test(style.overflowX) && element.scrollWidth > element.clientWidth) return true;
    if (element === boundary) break;
  }
  return false;
}

/** Stop sideways page drags without disabling vertical scroll, zoom or widgets. */
export default function useHorizontalPageScrollLock(mainRef, enabled, routeKey) {
  useEffect(() => {
    const main = mainRef.current;
    if (!enabled || !main || !window.matchMedia) return undefined;
    const boundary = main.closest('.App') || main;
    const mobile = window.matchMedia('(max-width: 760px)');
    let gesture = null;
    const reset = () => { gesture = null; };

    const onStart = event => {
      reset();
      if (!mobile.matches || event.touches.length !== 1 || hasTextSelection()
        || ownsHorizontalGesture(event.target, boundary)) return;
      const touch = event.touches[0];
      gesture = { id: touch.identifier, x: touch.clientX, y: touch.clientY, axis: null };
    };

    const onMove = event => {
      if (!gesture) return;
      if (!mobile.matches || event.touches.length !== 1 || hasTextSelection()) {
        reset();
        return;
      }
      const touch = Array.from(event.touches).find(item => item.identifier === gesture.id);
      if (!touch) {
        reset();
        return;
      }
      if (!gesture.axis) {
        const dx = Math.abs(touch.clientX - gesture.x);
        const dy = Math.abs(touch.clientY - gesture.y);
        if (Math.max(dx, dy) < GESTURE_THRESHOLD) return;
        gesture.axis = dx > dy ? 'horizontal' : 'vertical';
      }
      if (gesture.axis === 'horizontal' && event.cancelable) event.preventDefault();
    };

    boundary.addEventListener('touchstart', onStart, { passive: true });
    boundary.addEventListener('touchmove', onMove, { passive: false });
    boundary.addEventListener('touchend', reset);
    boundary.addEventListener('touchcancel', reset);
    window.addEventListener('blur', reset);
    document.addEventListener('visibilitychange', reset);
    return () => {
      reset();
      boundary.removeEventListener('touchstart', onStart);
      boundary.removeEventListener('touchmove', onMove);
      boundary.removeEventListener('touchend', reset);
      boundary.removeEventListener('touchcancel', reset);
      window.removeEventListener('blur', reset);
      document.removeEventListener('visibilitychange', reset);
    };
  }, [mainRef, enabled, routeKey]);
}
